import { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n2 } from "../../lib/format";
import DataTable from "../../lib/DataTable";
import { Notice } from "../../lib/ui";
import "./inventory.css";

/*
 * Update stock, retail & wholesale prices – port of adjust_stock.php (API: /api/stock/adjust, backend/stock.py)
 * Edit any rows, then save. Only changed rows are sent. After saving, the server compares stock value
 * (purchase price × quantity) with account 1200 and records an 'Adjustment' payment if they differ, as PHP did.
 */

export default function AdjustStock() {
  const api = useApi();
  const [rows, setRows] = useState(null);
  const [edits, setEdits] = useState({});
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { setRows(await api.get("/api/stock/adjust")); setEdits({}); }
    catch (e) { setNotice({ tone: "err", text: e.message }); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const byId = useMemo(() => Object.fromEntries((rows || []).map((r) => [r.product_id, r])), [rows]);
  const val = (r, k) => edits[r.product_id]?.[k] ?? String(r[k]);
  const setVal = (id, k, v) => setEdits((e) => ({ ...e, [id]: { ...e[id], [k]: v } }));
  const changed = Object.entries(edits).filter(([id, e]) => {
    const r = byId[id];
    return r && Object.entries(e).some(([k, v]) => Number(v) !== Number(r[k]));
  });

  const save = async () => {
    setSaving(true); setNotice(null);
    try {
      const payload = changed.map(([id]) => {
        const r = byId[id];
        return { product_id: Number(id), quantity: Number(val(r, "quantity")), retail_price: Number(val(r, "retail_price")), wholesale_price: Number(val(r, "wholesale_price")) };
      });
      const res = await api.post("/api/stock/adjust", { rows: payload });
      setNotice({ tone: res.tone || "ok", text: res.message });
      load();
    } catch (e) { setNotice({ tone: "err", text: e.message }); }
    finally { setSaving(false); }
  };

  const input = (r, k, step) => (
    <input className="cell" type="number" min="0" step={step} value={val(r, k)} aria-label={`${r.product_name} new ${k.replace("_", " ")}`}
      onChange={(e) => setVal(r.product_id, k, e.target.value)} style={{ maxWidth: "8rem" }} />
  );
  const columns = [
    { key: "product_id", label: "ID", num: true },
    { key: "product_name", label: "Name", render: (r) => <b style={{ color: "var(--ink)" }}>{r.product_name}</b> },
    { key: "manufacturer", label: "Manufacturer" },
    { key: "product_type", label: "Type" },
    { key: "quantity", label: "Current qty", num: true },
    { key: "new_qty", label: "New qty", sortable: false, render: (r) => input(r, "quantity", "1") },
    { key: "retail_price", label: "Retail", num: true, render: (r) => n2(r.retail_price) },
    { key: "new_retail", label: "New retail", sortable: false, render: (r) => input(r, "retail_price", "0.01") },
    { key: "wholesale_price", label: "Wholesale", num: true, render: (r) => n2(r.wholesale_price) },
    { key: "new_wholesale", label: "New wholesale", sortable: false, render: (r) => input(r, "wholesale_price", "0.01") },
  ];

  const actions = (
    <>
      {changed.length > 0 && <button type="button" className="btn" onClick={() => setEdits({})}>Discard changes</button>}
      <button type="button" className="btn primary" disabled={!changed.length || saving} onClick={save}>
        <i className="fas fa-floppy-disk" /> {saving ? "Saving…" : `Save adjustments${changed.length ? ` (${changed.length})` : ""}`}
      </button>
    </>
  );

  return (
    <main className="pg" style={{ maxWidth: "96rem" }}>
      <PageHeader slug="adjust_stock" actions={actions} />
      <Notice notice={notice} onClose={() => setNotice(null)} />
      <section className="panel">
        {rows ? <DataTable columns={columns} rows={rows} rowKey={(r) => r.product_id} searchPlaceholder="Search products" initialSort={{ key: "product_id", dir: 1 }} />
          : <div className="empty"><i className="fas fa-spinner fa-spin" />Loading products…</div>}
      </section>
    </main>
  );
}
